import Button from 'components/ui/Button';
import { useToast } from 'contexts/ToastContext';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  Mail,
  MousePointer,
} from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getEmailViewRequest,
  selectEmailViewDetails,
  selectEmailViewError,
  selectEmailViewLoading,
} from 'store/emailSentHistory';
import { IEmailSentHistory } from 'types/emailSentHistory/emailSentHistory.types';
import { cn } from 'utils/cn';

const EmailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const loading = useSelector(selectEmailViewLoading);
  const error = useSelector(selectEmailViewError);
  const emailDetails = useSelector(
    selectEmailViewDetails
  ) as IEmailSentHistory | null;

  const [emailHtml, setEmailHtml] = useState<string | null>(null);
  const [htmlLoading, setHtmlLoading] = useState<boolean>(false);
  const [htmlError, setHtmlError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(getEmailViewRequest(id));
    }
  }, [id, dispatch]);

  // Handle errors
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
    }
  }, [error, showToast]);

  // Fetch HTML content from filePath
  useEffect(() => {
    if (emailDetails?.filePath) {
      setHtmlLoading(true);
      setHtmlError(null);
      setEmailHtml(null);

      fetch(emailDetails.filePath)
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Failed to load email preview: ${response.statusText}`
            );
          }
          return response.text();
        })
        .then((html) => {
          setEmailHtml(html);
          setHtmlLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching email HTML:', err);
          setHtmlError(err.message || 'Failed to load email preview');
          setHtmlLoading(false);
        });
    }
  }, [emailDetails?.filePath]);

  const handleBack = () => {
    navigate(-1);
  };

  // Helper function to determine status
  const getEmailStatus = (emailHistory: IEmailSentHistory): string => {
    if (emailHistory.error || emailHistory.event?.failed) return 'failed';
    if (emailHistory.complaintAt) return 'complained';
    if (emailHistory.unsubscribedAt) return 'unsubscribed';
    if (emailHistory.spamAt) return 'spam';
    if (emailHistory.bouncedAt) return 'bounced';
    if (emailHistory.clickedEvents && emailHistory.clickedEvents.length > 0)
      return 'clicked';
    if (
      emailHistory.humanOpenedAt ||
      (emailHistory.openedEvents && emailHistory.openedEvents.length > 0)
    )
      return 'opened';
    if (emailHistory.deliveredAt) return 'delivered';
    if (emailHistory.sentAt) return 'sent';
    return 'pending';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'bg-success-background text-success border-success/30';
      case 'opened':
        return 'bg-primary-background text-primary border-primary/30';
      case 'clicked':
        return 'bg-info-background text-info border-info/30';
      case 'bounced':
        return 'bg-destructive-background text-destructive border-destructive/30';
      case 'complained':
      case 'draft':
        return 'bg-warning-background text-warning border-warning/30';
      case 'unsubscribed':
        return 'bg-warning-background text-warning border-warning/30';
      case 'spam':
      case 'failed':
        return 'bg-destructive-background text-destructive border-destructive/30';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'opened':
        return <Eye className="w-4 h-4" />;
      case 'clicked':
        return <MousePointer className="w-4 h-4" />;
      case 'bounced':
      case 'complained':
      case 'failed':
      case 'spam':
        return <AlertCircle className="w-4 h-4" />;
      case 'unsubscribed':
        return <AlertCircle className="w-4 h-4" />;
      case 'draft':
        return <Clock className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'opened':
        return 'Opened';
      case 'clicked':
        return 'Clicked';
      case 'bounced':
        return 'Bounced';
      case 'complained':
        return 'Complaint';
      case 'unsubscribed':
        return 'Unsubscribed';
      case 'spam':
        return 'Spam';
      case 'failed':
        return 'Failed';
      case 'draft':
        return 'Draft';
      default:
        return 'Pending';
    }
  };

  // Helper function to format date
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return 'N/A';
    const momentDate = moment(date);
    if (!momentDate.isValid()) return 'Invalid date';
    return momentDate.format('MMM D, YYYY h:mm A');
  };

  // Helper component for detail fields
  const DetailField: React.FC<{
    label: string;
    icon: React.ReactNode;
    value: React.ReactNode;
    colSpan?: number;
  }> = ({ label, icon, value, colSpan = 1 }) => (
    <div className={`space-y-1 ${colSpan === 2 ? 'md:col-span-2' : ''}`}>
      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
      </label>
      <div className="flex items-center gap-2 text-sm text-foreground">
        {icon}
        <span>{value}</span>
      </div>
    </div>
  );

  // Helper function to render email preview using early returns/guard clauses
  const renderEmailPreview = (): React.ReactNode => {
    // Guard clause: No email details available
    if (!emailDetails) {
      return null;
    }

    // Guard clause: No file path available
    if (!emailDetails.filePath) {
      return (
        <div className="border-2 border-dashed border-border rounded-xl p-16 text-center bg-muted/30">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center ring-4 ring-background">
              <Mail className="w-10 h-10 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No Preview Available
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Email preview is not available for this email.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Guard clause: Loading state
    if (htmlLoading) {
      return (
        <div className="border-2 border-dashed border-border rounded-xl p-16 text-center bg-muted/30">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
              <div className="absolute inset-0 bg-primary-muted rounded-full blur-xl opacity-50" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                Loading email preview…
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Please wait while we fetch the content
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Guard clause: Error state
    if (htmlError) {
      return (
        <div className="border-2 border-dashed border-destructive/50 rounded-xl p-16 text-center bg-destructive-background/50">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="w-16 h-16 bg-destructive-background rounded-full flex items-center justify-center ring-4 ring-destructive/20">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-destructive mb-2">
                Error Loading Preview
              </h3>
              <p className="text-sm text-destructive max-w-md bg-card/60 rounded-lg px-4 py-2 inline-block">
                {htmlError}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Guard clause: No HTML content available
    if (!emailHtml) {
      return (
        <div className="border-2 border-dashed border-border rounded-xl p-16 text-center bg-muted/30">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center ring-4 ring-background">
              <Mail className="w-10 h-10 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No Preview Available
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Email preview is not available for this email.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Default case: Render email HTML content
    return (
      <div className="relative">
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="overflow-auto max-h-[600px] min-h-[400px] bg-card scrollbar-thin">
            <div
              className="font-sans antialiased text-card-foreground p-6 [&_*]:max-w-full [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_table]:w-full [&_table]:border-collapse [&_a]:text-primary [&_a]:no-underline hover:[&_a]:underline [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_p]:leading-relaxed [&_p]:mb-4"
              dangerouslySetInnerHTML={{ __html: emailHtml }}
            />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col h-full bg-background">
        <div className="p-6 pb-0">
          <div className="space-y-2">
            <Button
              variant="ghost"
              size="medium"
              onClick={handleBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-foreground">Email View</h1>
            <p className="text-sm text-muted-foreground">
              Loading email details…
            </p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">
              Loading email details…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !emailDetails) {
    return (
      <div className="flex-1 flex flex-col h-full bg-background">
        <div className="p-6 pb-0">
          <div className="space-y-2">
            <Button
              variant="ghost"
              size="medium"
              onClick={handleBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-foreground">Email View</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-destructive-background flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Error Loading Email
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              {error || 'Email not found'}
            </p>
            <Button variant="primary" size="medium" onClick={handleBack}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const emailStatus = getEmailStatus(emailDetails);

  return (
    <div className="flex-1 flex flex-col h-full bg-background">
      {/* Header – aligned with DomainRecords / SegmentDetails */}
      <div className="p-6 pb-0">
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="medium"
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Email View</h1>
          <p className="text-sm text-muted-foreground">
            Details and preview for this sent email
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Email Details Card */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Email Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Status
                </label>
                <div>
                  <span
                    className={cn(
                      'inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-sm font-medium border',
                      getStatusColor(emailStatus)
                    )}
                  >
                    {getStatusIcon(emailStatus)}
                    <span>{getStatusText(emailStatus)}</span>
                  </span>
                </div>
              </div>

              <DetailField
                label="Email Address"
                icon={<Mail className="w-4 h-4 text-muted-foreground" />}
                value={emailDetails.email || 'N/A'}
              />

              {emailDetails.from && (
                <DetailField
                  label="From"
                  icon={<Mail className="w-4 h-4 text-muted-foreground" />}
                  value={emailDetails.from}
                />
              )}

              {emailDetails.createdAt && (
                <DetailField
                  label="Created At"
                  icon={<Calendar className="w-4 h-4 text-muted-foreground" />}
                  value={formatDate(emailDetails.createdAt)}
                />
              )}

              {emailDetails.sentAt && (
                <DetailField
                  label="Sent At"
                  icon={<Mail className="w-4 h-4 text-muted-foreground" />}
                  value={formatDate(emailDetails.sentAt)}
                />
              )}

              {emailDetails.deliveredAt && (
                <DetailField
                  label="Delivered At"
                  icon={
                    <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  }
                  value={formatDate(emailDetails.deliveredAt)}
                />
              )}

              {emailDetails.openedEvents &&
                emailDetails.openedEvents.length > 0 && (
                  <DetailField
                    label="Opened Events"
                    icon={<Eye className="w-4 h-4 text-muted-foreground" />}
                    value={`${emailDetails.openedEvents.length} time${emailDetails.openedEvents.length > 1 ? 's' : ''}`}
                  />
                )}

              {emailDetails.clickedEvents &&
                emailDetails.clickedEvents.length > 0 && (
                  <DetailField
                    label="Clicked Events"
                    icon={
                      <MousePointer className="w-4 h-4 text-muted-foreground" />
                    }
                    value={`${emailDetails.clickedEvents.length} time${emailDetails.clickedEvents.length > 1 ? 's' : ''}`}
                  />
                )}

              {emailDetails.error && (
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Error
                  </label>
                  <div className="flex items-start gap-2 text-sm text-destructive bg-destructive-background p-3 rounded-lg border border-destructive/30">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{emailDetails.error}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Email Preview Section */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              Email Preview
            </h2>
            {renderEmailPreview()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailView;
